import React, { useState } from "react";
import { Box, Button, Grid, useTheme } from "@mui/material";
import useForm, { UseFormProps } from "../../../../hooks/useForm";
import _ from "lodash";
import FormElement, { FormElementPropsWithoutProduct } from "../FormElement";
import addressFields from "../../../../services/Product/FormDefinitions/modules/addressFields";
import caseFullFormStyle from "../Full/caseFullFormStyle";
import ApiClient from "../../../../services/ApiClient";
import {
  persistAddress,
  persistCasePerson,
  persistPerson,
  sanitizePersonData,
} from "../../../../services/Persist/persisterService";
import { translate } from "../../../../services/Translations/translatorService";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonLoading from "../../../Button/ButtonLoading";
import {
  emailDefault,
  isPhoneNumberWithoutSpecialCharactersDefault,
  requiredFieldDefault,
} from "../../../../services/validationRules";
import Typography from "@mui/material/Typography";
import { Person } from "../../../../types/Person";
import { UseForm } from "../../../../types/UseForm";
import { useSnackbar } from "notistack";
import { ProductUnion } from "../../../../types/ProductUnion";

type ProcessParticipantPersonFormProps = {
  product: ProductUnion;
  participant?: Person;
  refreshPage: Function;
};
const ProcessParticipantPersonForm = ({ product, participant, refreshPage }: ProcessParticipantPersonFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [buttonIsLoading, setButtonIsLoading] = useState(false);

  const getParticipantLabelTypeOptions = (product: ProductUnion) => {
    const options = [
      { value: "expert" },
      { value: "witness" },
      { value: "legalRepresentative" },
      { value: "judge" },
      { value: "interpreter" },
      { value: "insolvencyAdministrator" },
      { value: "opposingPartyRepresentative" },
    ];

    if (product.productClassName === "rentalContract") {
      options.push({ value: "additionalOpposingParty" });
    }

    options.push({ value: "other" });

    return options;
  };


  const formElements: FormElementPropsWithoutProduct[] = [
    {
      type: "ValidatorSelect",
      path: "participant.labelType",
      validators: requiredFieldDefault,
      options: () => getParticipantLabelTypeOptions(product),
    },
    {
      type: "ValidatorTextField",
      path: `participant.email`,
      validators: emailDefault,
    },
    {
      type: "ValidatorTextField",
      path: `participant.telephone`,
      validators: isPhoneNumberWithoutSpecialCharactersDefault,
    },
    {
      type: "ValidatorSelect",
      path: `participant.gender`,
      validators: requiredFieldDefault,
      options: () => [{ value: "male" }, { value: "female" }],
    },
    {
      type: "ValidatorTextField",
      path: `participant.givenName`,
      validators: requiredFieldDefault,
    },
    {
      type: "ValidatorTextField",
      path: `participant.familyName`,
      validators: requiredFieldDefault,
    },
    ...addressFields({ path: "participant.residenceAddress" }),
    {
      type: "ValidatorTextField",
      path: `participant.casePerson.reference`,
    },
    {
      type: "ValidatorTextField",
      path: `participant.beaSafeId`,
    },
  ];

  const deleteParticipant = async () => {
    setIsLoading(true);
    await ApiClient.delete("people/" + participant!.id);
    await refreshPage();
    setIsLoading(false);
  };

  const persistPersonSubEntities = async (processParticipant: Person, values: UseForm["values"]) => {
    await persistAddress(processParticipant.residenceAddress.id, values.participant.residenceAddress);
    if (processParticipant.casePerson?.id) {
      await persistCasePerson(processParticipant.casePerson.id, {
        reference: values.participant.casePerson.reference,
      });
    }
  };

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    const participantData = _.merge({}, values.participant, {
      residenceAddress: undefined,
      type: "processParticipant",
      customer: product.customer!["@id"],
      caseId: product.id,
    });

    let createdParticipantPerson;

    if (!participant) {
      setIsLoading(true);
      createdParticipantPerson = await ApiClient.post("people", {
        body: JSON.stringify(sanitizePersonData(participantData)),
      });
      await persistPersonSubEntities(createdParticipantPerson, values);
    } else {
      setButtonIsLoading(true);
      await persistPerson(participant.id, participantData);
      await persistPersonSubEntities(participant, values);
    }
    await refreshPage();
    if (createdParticipantPerson) {
      enqueueSnackbar("Prozessbeteiligte Person erfolgreich gespeichert", {
        variant: "custom",
        buttonType: "link",
        buttonLink: "#processParticipantPerson" + createdParticipantPerson.id,
        buttonTarget: "_self",
        buttonText: "Zur Person",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 10000,
      });
    }
    setIsLoading(false);
    setButtonIsLoading(false);
  };

  const initialValues = {
    participant: _.merge(
      {},
      participant || {
        labelType: "",
        gender: "",
        givenName: "",
        familyName: "",
        email: "",
        telephone: "",
        residenceAddress: {
          streetAddress: "",
          postalCode: "",
          addressLocality: "",
        },
        casePerson: {
          reference: "",
        },
      }
    ),
  };

  const useFormProps = useForm({
    initialValues,
    onSubmit,
    identifier: product.id + "-" + (participant && participant.id),
    clearFormAfterSubmit: !participant,
  });

  const getLabel = (participant: Person | undefined) => {
    if (!participant) {
      return "Neue Person hinzufügen";
    }

    const labelType = translate("participant.labelType.values." + participant.labelType);
    const gender = translate("participant.gender.values." + participant.gender);
    return labelType + ": " + gender + " " + participant.givenName + " " + participant.familyName;
  };

  if (isLoading) {
    return (
      <Box sx={caseFullFormStyle.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container sx={caseFullFormStyle.grid}>
      <Grid size={12}>
        <Typography variant={"h4"} sx={{ mt: 1, mb: 1 }}>
          {getLabel(participant)}
        </Typography>
      </Grid>
      {_.map(formElements, (formElementProps) => {
        if (formElementProps.isHidden && formElementProps.isHidden({ values: useFormProps.values, product })) {
          return null;
        }

        const fullWidth = formElementProps.additionalProps && formElementProps.additionalProps.fullWidth;
        return (
          <Grid
            key={formElementProps.path}
            size={{
              xs: 12,
              md: !fullWidth && 6,
              lg: !fullWidth && 4,
            }}
          >
            <FormElement product={product} {...formElementProps} {...useFormProps} />
          </Grid>
        );
      })}
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      />
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        {participant && (
          <Button fullWidth onClick={deleteParticipant}>
            Entfernen
          </Button>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <ButtonLoading fullWidth variant="contained" onClick={useFormProps.handleSubmit} isLoading={buttonIsLoading}>
          Speichern
        </ButtonLoading>
      </Grid>
    </Grid>
  );
};
export default ProcessParticipantPersonForm;
